import { useEffect, useMemo, useState } from "react";
import urls from "@/content/siteUrls.json";
import { Download, RotateCcw, ExternalLink } from "lucide-react";

const DISPOSITIONS = ["Undecided", "Migrate", "Consolidate", "Archive", "Redirect", "Retire"];
const TYPES = ["all", "page", "research", "people", "news", "events"];
const TYPE_LABELS = {
  all: "All",
  page: "Pages",
  research: "Research",
  people: "People",
  news: "News",
  events: "Events",
};
const STORE_KEY = "harc-triage-v1";
const THREE_YEARS_AGO = "2023-08-26";

const suggest = (item) => {
  if (item.type === "events") return "Retire — stale since Jan 2025";
  if (item.type === "news")
    return item.lastmod && item.lastmod < THREE_YEARS_AGO
      ? "Archive candidate — over 3 yrs, verify traffic"
      : "Migrate candidate — recent";
  if (item.type === "research") return "Review — top items become Impact Stories";
  if (item.type === "people") return "Migrate — verify still current";
  return "Review — map to new sitemap";
};

const slug = (url) => url.replace("https://harcresearch.org", "") || "/";

export default function Triage() {
  const [decisions, setDecisions] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORE_KEY)) || {};
    } catch {
      return {};
    }
  });
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    localStorage.setItem(STORE_KEY, JSON.stringify(decisions));
  }, [decisions]);

  const filtered = useMemo(
    () =>
      urls.filter(
        (u) =>
          (type === "all" || u.type === type) &&
          (!q || u.url.toLowerCase().includes(q.toLowerCase()))
      ),
    [type, q]
  );

  const decidedCount = Object.values(decisions).filter((d) => d && d !== "Undecided").length;
  const counts = useMemo(() => {
    const c = {};
    Object.values(decisions).forEach((d) => {
      if (d && d !== "Undecided") c[d] = (c[d] || 0) + 1;
    });
    return c;
  }, [decisions]);

  const exportCsv = () => {
    const rows = [
      "url,type,lastmod,suggestion,decision",
      ...urls.map((u) =>
        [u.url, u.type, u.lastmod, `"${suggest(u)}"`, decisions[u.url] || "Undecided"].join(",")
      ),
    ];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "harc-content-triage.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="triage-page" data-testid="triage-worksheet">
      <div className="triage-head">
        <p className="masthead-kicker">Phase 1 — Migration Strategy Tool</p>
        <h1 className="masthead-title triage-title">Content Triage Worksheet</h1>
        <p className="masthead-sub">
          All {urls.length} URLs from the live harcresearch.org sitemaps. Mark each item Migrate,
          Consolidate, Archive, Redirect, or Retire. Suggestions are heuristics from the audit — not
          decisions. Your marks save in this browser; export CSV to share with the team.
        </p>
      </div>

      <div className="triage-summary" data-testid="triage-summary">
        <span className="summary-progress" data-testid="triage-progress">
          {decidedCount} / {urls.length} decided
        </span>
        {DISPOSITIONS.slice(1).map((d) => (
          <span key={d} className={`summary-chip chip-${d.toLowerCase()}`}>
            {d}: {counts[d] || 0}
          </span>
        ))}
        <span className="summary-actions">
          <button
            type="button"
            className="triage-btn"
            data-testid="triage-export-button"
            onClick={exportCsv}
          >
            <Download size={14} strokeWidth={2.2} aria-hidden="true" /> Export CSV
          </button>
          <button
            type="button"
            className="triage-btn triage-btn-ghost"
            data-testid="triage-reset-button"
            onClick={() => setDecisions({})}
          >
            <RotateCcw size={14} strokeWidth={2.2} aria-hidden="true" /> Reset
          </button>
        </span>
      </div>

      <div className="triage-controls">
        <div className="triage-tabs" role="tablist" aria-label="Filter by content type">
          {TYPES.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={type === t}
              data-testid={`triage-tab-${t}`}
              className={`triage-tab ${type === t ? "tab-active" : ""}`}
              onClick={() => setType(t)}
            >
              {TYPE_LABELS[t]}
              <span className="tab-count">
                {t === "all" ? urls.length : urls.filter((u) => u.type === t).length}
              </span>
            </button>
          ))}
        </div>
        <input
          type="search"
          className="triage-search"
          data-testid="triage-search-input"
          placeholder="Filter by URL keyword…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Filter URLs by keyword"
        />
      </div>

      <div className="doc-table-wrap triage-table-wrap">
        <table className="doc-table triage-table">
          <thead>
            <tr>
              <th>URL</th>
              <th>Type</th>
              <th>Last modified</th>
              <th>Suggested</th>
              <th>Decision</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.url}>
                <td className="triage-url">
                  <a href={u.url} target="_blank" rel="noopener noreferrer">
                    {slug(u.url)}
                    <ExternalLink size={11} aria-hidden="true" className="ext-icon" />
                  </a>
                </td>
                <td>{TYPE_LABELS[u.type]}</td>
                <td>{u.lastmod || "—"}</td>
                <td className="triage-suggestion">{suggest(u)}</td>
                <td>
                  <select
                    className="triage-select"
                    data-testid={`triage-select-${slug(u.url).replace(/[^a-z0-9]+/gi, "-")}`}
                    value={decisions[u.url] || "Undecided"}
                    onChange={(e) => setDecisions({ ...decisions, [u.url]: e.target.value })}
                    aria-label={`Disposition for ${slug(u.url)}`}
                  >
                    {DISPOSITIONS.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p className="triage-empty" data-testid="triage-empty">
            No URLs match this filter.
          </p>
        )}
      </div>
    </div>
  );
}
